import { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CalendarBlank, X, DotsThreeVertical } from '@phosphor-icons/react';
import { Calendar as DateCalendar } from 'react-date-range';
import { es } from 'date-fns/locale';
import WeeklyCalendarShell from './WeeklyCalendarShell';
import WeekRangeBar from './WeekRangeBar';
import { NotificationAlert } from '../../../shared/components/ui';
import TechniciansAvatarGroup from './TechniciansAvatarGroup';
import {
  getCalendarWeeks,
  startOfDay,
  dayDiff,
  toYmd,
  formatWeekShort,
} from '../utils/weeklyCalendar';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

function formatWeek(start, end) {
  return formatWeekShort(start, end);
}

export function GuardsCalendar({
  teams,
  teamColorMap,
  onDutyRecords,
  initialMonth,
  initialYear,
  onMonthChange,
  techniciansByTeamId = {},
  onAssignWeek,
  onRemoveWeek,
  onInterruptGuard,
  onRestoreGuard,
  onDeleteGuard,
  isMutating,
  isLoading = false,
  canManage = false,
}) {
  const now = new Date();
  const [month, setMonth] = useState(initialMonth ?? now.getMonth());
  const [year, setYear] = useState(initialYear ?? now.getFullYear());
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [isInterruptMode, setIsInterruptMode] = useState(false);
  const [interruptReason, setInterruptReason] = useState('');
  const [interruptTeamId, setInterruptTeamId] = useState('');
  const [interruptEndAt, setInterruptEndAt] = useState('');
  const [isInterruptDateOpen, setIsInterruptDateOpen] = useState(false);
  const [modalError, setModalError] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverRecord, setPopoverRecord] = useState(null);
  const [popoverWeek, setPopoverWeek] = useState(null);
  const [popoverPos, setPopoverPos] = useState({ x: 0, y: 0 });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const popoverRef = useRef(null);
  const interruptDatePopupRef = useRef(null);
  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const weeks = useMemo(() => getCalendarWeeks(year, month), [year, month]);

  const activeWeekIdx = useMemo(() => {
    return weeks.findIndex((w) => now >= w.weekStart && now <= w.weekEnd);
  }, [weeks, now]);

  const assignedRecordByWeek = useMemo(() => {
    const map = {};

    weeks.forEach((week) => {
      const records = onDutyRecords
        .filter((record) => {
          if (!record.startDate) return false;
          const startDate = startOfDay(record.startDate);
          return startDate >= week.weekStart && startDate <= week.weekEnd;
        })
        .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

      map[week.weekNumber] = records;
    });
    return map;
  }, [onDutyRecords, weeks]);

  const changeMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    const nextYear = d.getFullYear();
    const nextMonth = d.getMonth();
    setYear(nextYear);
    setMonth(nextMonth);
    if (onMonthChange) {
      onMonthChange(nextMonth, nextYear);
    }
  };

  useEffect(() => {
    if (typeof initialMonth === 'number') {
      setMonth(initialMonth);
    }
  }, [initialMonth]);

  useEffect(() => {
    if (typeof initialYear === 'number') {
      setYear(initialYear);
    }
  }, [initialYear]);

  const openAssignModal = (week, record = null) => {
    const assignedRecords = assignedRecordByWeek[week.weekNumber];
    const recordToUse = record || assignedRecords?.[0] || null;
    setSelectedWeek(week);
    setSelectedRecord(recordToUse);
    setSelectedTeamId(recordToUse?.teamId || '');
    setModalError('');
    setIsAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedWeek(null);
    setSelectedRecord(null);
    setSelectedTeamId('');
    setIsInterruptMode(false);
    setInterruptReason('');
    setInterruptTeamId('');
    setInterruptEndAt('');
    setIsInterruptDateOpen(false);
    setModalError('');
  };

  const openInterruptMode = () => {
    const currentRecord = selectedRecord || assignedRecordByWeek[selectedWeek?.weekNumber]?.[0];
    const currentStart = currentRecord?.startAt || toYmd(selectedWeek?.weekStart || now);
    const currentEnd = currentRecord?.endAt || toYmd(selectedWeek?.weekEnd || now);
    const todayYmd = toYmd(startOfDay(now));
    const defaultEndAt = todayYmd < currentStart ? currentStart : (todayYmd > currentEnd ? currentEnd : todayYmd);

    setInterruptTeamId(currentRecord?.teamId || '');
    setInterruptReason('');
    setInterruptEndAt(defaultEndAt);
    setModalError('');
    setIsInterruptMode(true);
  };

  const closeInterruptMode = () => {
    setIsInterruptMode(false);
    setInterruptReason('');
    setInterruptTeamId('');
    setInterruptEndAt('');
    setIsInterruptDateOpen(false);
    setModalError('');
  };

  useEffect(() => {
    if (!isInterruptDateOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (interruptDatePopupRef.current && !interruptDatePopupRef.current.contains(event.target)) {
        setIsInterruptDateOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isInterruptDateOpen]);

  const parseYmd = (value) => {
    if (!value || typeof value !== 'string') return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatDisplayDate = (value) => {
    const date = parseYmd(value);
    if (!date) return 'dd/mm/aaaa';
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isGuardInterruptible = (record) => {
    if (!record?.startDate) return false;
    const today = startOfDay(now);
    const end = startOfDay(record.endDate || new Date(8640000000000000));
    return today <= end && !record?.isInterrupted;
  };

  const isGuardRestorable = (record, week) => {
    if (!record?.isInterrupted || !record?.startDate || !week?.weekEnd) return false;
    const today = startOfDay(now);
    return today <= startOfDay(week.weekEnd) && record.endAt !== toYmd(week.weekEnd);
  };

  const openGuardPopover = (week, record, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pw = 320;
    const ph = 220;
    let x = rect.right + 8;
    let y = rect.top;
    if (x + pw > vw) x = rect.left - pw - 8;
    if (y + ph > vh) y = vh - ph - 16;
    if (x < 8) x = 8;
    if (y < 8) y = 8;
    setPopoverPos({ x, y });
    setPopoverWeek(week);
    setPopoverRecord(record);
    setIsPopoverOpen(true);
    setIsMenuOpen(false);
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
    setPopoverRecord(null);
    setPopoverWeek(null);
    setIsMenuOpen(false);
  };

  const handlePopoverInterrupt = () => {
    const record = popoverRecord;
    const week = popoverWeek;
    if (!isGuardInterruptible(record)) {
      closePopover();
      return;
    }
    setSelectedWeek(week);
    setSelectedRecord(record);
    closePopover();
    const currentStart = record?.startAt || toYmd(week?.weekStart || now);
    const currentEnd = record?.endAt || toYmd(week?.weekEnd || now);
    const todayYmd = toYmd(startOfDay(now));
    const defaultEndAt = todayYmd < currentStart ? currentStart : (todayYmd > currentEnd ? currentEnd : todayYmd);
    setInterruptTeamId(record?.teamId || '');
    setInterruptReason('');
    setInterruptEndAt(defaultEndAt);
    setModalError('');
    setIsInterruptMode(true);
    setIsAssignModalOpen(true);
  };

  const handlePopoverRemove = () => {
    if (popoverRecord) {
      onDeleteGuard(popoverRecord);
    }
    closePopover();
  };

  const handlePopoverRestore = async () => {
    if (!popoverRecord || !popoverWeek) return;

    try {
      await onRestoreGuard({
        currentRecord: popoverRecord,
        endAt: toYmd(popoverWeek.weekEnd),
      });
      closePopover();
    } catch (err) {
      setModalError(err.message || 'No se pudo restaurar la guardia');
    }
  };

  const handleAssignTeam = async () => {
    if (!selectedWeek || !selectedTeamId) return;

    const assignedRecords = assignedRecordByWeek[selectedWeek.weekNumber];

    try {
      await onAssignWeek({
        teamId: selectedTeamId,
        startAt: toYmd(selectedWeek.weekStart),
        endAt: toYmd(selectedWeek.weekEnd),
        existingRecords: assignedRecords,
      });
      closeAssignModal();
    } catch (err) {
      setModalError(err.message || 'No se pudo asignar la guardia');
    }
  };

  const handleInterruptTeam = async () => {
    const missingFields = [];
    if (!selectedWeek) missingFields.push('semana');
    if (!interruptEndAt) missingFields.push('fecha de interrupcion');
    if (!interruptReason.trim()) missingFields.push('motivo');
    if (!interruptTeamId) missingFields.push('nuevo equipo de guardia');

    if (missingFields.length > 0) {
      setModalError(`Falta completar: ${missingFields.join(', ')}`);
      return;
    }

    const currentRecord = selectedRecord || assignedRecordByWeek[selectedWeek.weekNumber]?.[0];
    if (!currentRecord) {
      setModalError('No hay una guardia vigente para interrumpir en esta semana');
      return;
    }

    if (!isGuardInterruptible(currentRecord)) {
      setModalError('Solo se puede interrumpir una guardia vigente (actual o futura)');
      return;
    }

    if (interruptTeamId === currentRecord.teamId) {
      setModalError('Debes seleccionar un equipo diferente al de la guardia actual');
      return;
    }

    const currentStart = currentRecord.startAt || toYmd(selectedWeek.weekStart);
    const originalEnd = currentRecord.endAt || toYmd(selectedWeek.weekEnd);

    if (interruptEndAt < currentStart || interruptEndAt > originalEnd) {
      setModalError(`La fecha de interrupcion debe estar entre ${currentStart} y ${originalEnd}`);
      return;
    }

    try {
      await onInterruptGuard({
        currentRecord,
        reason: interruptReason.trim(),
        supportTeamId: interruptTeamId,
        startAt: currentStart,
        endAt: interruptEndAt,
      });
      closeAssignModal();
    } catch (err) {
      setModalError(err.message || 'No se pudo interrumpir la guardia');
    }
  };

  const teamAssignmentCount = useMemo(() => {
    const counter = {};
    Object.values(assignedRecordByWeek).forEach((records) => {
      if (!records || records.length === 0) return;
      records.forEach((record) => {
        counter[record.teamId] = (counter[record.teamId] || 0) + 1;
      });
    });
    return counter;
  }, [assignedRecordByWeek]);

  const isInterruptedRecord = Boolean(selectedRecord?.isInterrupted);
  const isFutureGuard = Boolean(selectedRecord?.startDate && selectedRecord.startDate > now);

  const legendItems = [
    { label: 'Guardia Terminada', color: 'var(--muted-deep)' },
    { label: 'Semana Actual', color: 'var(--success)' },
    { label: 'Guardia Futura', color: 'var(--amber)' },
  ];

  const barsByWeek = useMemo(() => {
    const map = {};

    weeks.forEach((week) => {
      const assignedRecords = assignedRecordByWeek[week.weekNumber] || [];
      const orderedForDisplay = [...assignedRecords].sort((a, b) => {
        const aStart = new Date(a.startDate).getTime();
        const bStart = new Date(b.startDate).getTime();
        if (aStart !== bStart) return aStart - bStart;

        const aEnd = new Date(a.endDate || new Date(8640000000000000)).getTime();
        const bEnd = new Date(b.endDate || new Date(8640000000000000)).getTime();
        if (aEnd !== bEnd) return aEnd - bEnd;

        const aCreated = new Date(a.createdAt || a.updatedAt || a.startDate).getTime();
        const bCreated = new Date(b.createdAt || b.updatedAt || b.startDate).getTime();
        return aCreated - bCreated;
      });

      map[week.weekNumber] = orderedForDisplay
        .map((record, recordIdx) => {
          const recordStart = startOfDay(record.startDate);
          const recordEnd = startOfDay(record.endDate || new Date(8640000000000000));
          const clampedStart = recordStart > week.weekStart ? recordStart : week.weekStart;
          const clampedEnd = recordEnd < week.weekEnd ? recordEnd : week.weekEnd;

          if (clampedStart > clampedEnd) {
            return null;
          }

          const startIndex = Math.max(0, Math.min(6, dayDiff(week.weekStart, clampedStart)));
          const endIndex = Math.max(startIndex, Math.min(6, dayDiff(week.weekStart, clampedEnd)));
          const team = teams.find((t) => t.id === record.teamId);

          return {
            key: record.id || `${week.weekNumber}-${record.teamId}-${recordIdx}`,
            record,
            team,
            startIndex,
            endIndex,
            rowIndex: recordIdx,
          };
        })
        .filter(Boolean);
    });

    return map;
  }, [weeks, assignedRecordByWeek, teams]);

  const barsHeightByWeek = useMemo(() => {
    const map = {};
    weeks.forEach((week) => {
      const bars = barsByWeek[week.weekNumber] || [];
      map[week.weekNumber] = bars.length > 0 ? (bars.length * 38) + 10 : 0;
    });
    return map;
  }, [weeks, barsByWeek]);

  return (
    <div className="space-y-6">
      <WeeklyCalendarShell
        month={month}
        year={year}
        weeks={weeks}
        onChangeMonth={changeMonth}
        activeWeekIdx={activeWeekIdx}
        legendItems={legendItems}
        getWeekRowClassName={({ weekIndex }) => (weekIndex === activeWeekIdx ? 'bg-surface-2' : 'bg-surface')}
        renderWeekLeft={({ week }) => {
          if (isLoading) {
            return (
              <div className="p-4 border-r border-line bg-surface-2">
                <div className="h-5 w-24 rounded bg-surface-2 animate-pulse" />
                <div className="mt-4 h-8 w-full rounded-lg bg-surface-2 animate-pulse" />
              </div>
            );
          }

          const assignedRecords = assignedRecordByWeek[week.weekNumber] || [];
          const hasAssignments = assignedRecords.length > 0;
          const today = startOfDay(now);
          const isCurrentWeek = today >= week.weekStart && today <= week.weekEnd;
          const hasActiveGuard = assignedRecords.some((record) => {
            if (!record?.startDate || record?.isInterrupted) return false;
            const recordStart = startOfDay(record.startDate);
            const recordEnd = startOfDay(record.endDate || new Date(8640000000000000));
            return recordStart <= today && today <= recordEnd;
          });
          const showAssignButton = !hasAssignments || (isCurrentWeek && !hasActiveGuard);

          return (
            <div className={`p-3 md:p-4 border-r border-line ${hasAssignments ? 'bg-surface-2' : ''}`}>
              <p className="text-content font-medium">
                <span className="md:hidden">S{week.weekNumber}</span>
                <span className="hidden md:inline">Semana {week.weekNumber}</span>
              </p>
              {canManage && showAssignButton && (
                <button
                  type="button"
                  onClick={() => openAssignModal(week)}
                  className="w-10 h-10 md:w-full md:h-auto mt-4 border border-dashed border-line text-content rounded-lg py-2 text-sm hover:bg-surface-2"
                >
                  <span className="md:hidden">+</span><span className="hidden md:inline">+ Asignar equipo</span>
                </button>
              )}
            </div>
          );
        }}
        getDayCell={({ day, isCurrentMonth, isToday, week }) => {
          if (isLoading) {
            return {
              className: 'px-4 py-4 border-r last:border-r-0 border-line',
              style: { minHeight: '108px' },
              content: (
                <div className="space-y-3">
                  <div className="h-5 w-6 rounded bg-surface-2 animate-pulse" />
                  <div className="h-2 w-full rounded bg-surface-2 animate-pulse" />
                </div>
              ),
            };
          }

          const barsHeight = barsHeightByWeek[week.weekNumber] || 0;

          return {
            className: 'px-0 md:px-4 py-4 border-r last:border-r-0 border-line text-center text-content font-semibold',
            style: { minHeight: `${70 + barsHeight}px` },
            content: isToday ? (
              <span className="relative -top-1 w-8 h-8 rounded-full bg-amber text-on-amber inline-flex align-middle items-center justify-center">{day.getDate()}</span>
            ) : (
              <span className={isCurrentMonth ? 'text-content' : 'text-content-muted'}>{day.getDate()}</span>
            ),
          };
        }}
        renderWeekOverlay={({ week }) => {
          if (isLoading) {
            return [
              <div
                key={`skeleton-bar-1-${week.weekNumber}`}
                className="absolute left-1 right-1 h-8 rounded-md bg-surface-2 animate-pulse"
                style={{ top: '48px' }}
              />,
              <div
                key={`skeleton-bar-2-${week.weekNumber}`}
                className="absolute left-1/4 right-2 h-8 rounded-md bg-surface-2 animate-pulse"
                style={{ top: '86px' }}
              />,
            ];
          }

          const bars = barsByWeek[week.weekNumber] || [];

          return bars.map((bar) => {
            const leftPercent = (bar.startIndex / 7) * 100;
            const widthPercent = ((bar.endIndex - bar.startIndex + 1) / 7) * 100;
            const recordStart = startOfDay(bar.record.startDate);
            const recordEnd = startOfDay(bar.record.endDate || new Date(8640000000000000));
            const today = startOfDay(now);

            const isActiveGuard = recordStart <= today && recordEnd >= today;

            let barColor;
            if (recordEnd < today) {
              barColor = 'var(--muted-deep)';
            } else if (isActiveGuard) {
              barColor = 'var(--success)';
            } else {
              barColor = 'var(--amber)';
            }

            return (
              <WeekRangeBar
                key={bar.key}
                onClick={(e) => openGuardPopover(week, bar.record, e)}
                top={`${48 + (bar.rowIndex * 38)}px`}
                leftPercent={leftPercent}
                widthPercent={widthPercent}
                backgroundColor={barColor}
                tinted={isActiveGuard}
                colorRgbVar="success-rgb"
                title={bar.team?.name || 'Equipo asignado'}
                label={bar.team?.name || 'Equipo asignado'}
              />
            );
          });
        }}
      />


      {portalTarget && isPopoverOpen && popoverRecord && popoverWeek && createPortal(
        <>
          <div className="fixed inset-0 z-[10000]" onClick={closePopover} />
          <div
            ref={popoverRef}
            className="fixed z-[10001] w-80 bg-surface rounded-xl shadow-2xl border border-line"
            style={{ top: popoverPos.y, left: popoverPos.x }}
          >
            <div className="flex items-center justify-end gap-1 px-3 pt-3">
              {canManage && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="w-8 h-8 rounded-full hover:bg-surface-2 inline-flex items-center justify-center text-content-muted hover:text-content transition-colors"
                  >
                    <DotsThreeVertical size={20} weight="bold" />
                  </button>
                  {isMenuOpen && (
                    <div className="absolute left-0 top-full mt-1 w-44 bg-surface-2 border border-line rounded-lg shadow-xl overflow-hidden z-10">
                      {(() => {
                        const popIsInterrupted = Boolean(popoverRecord?.isInterrupted);
                        const popCanInterrupt = !popIsInterrupted && isGuardInterruptible(popoverRecord);
                        const popCanRestore = popIsInterrupted && isGuardRestorable(popoverRecord, popoverWeek);
                        return (
                          <>
                            {popCanInterrupt && (
                              <button
                                type="button"
                                onClick={handlePopoverInterrupt}
                                className="w-full px-4 py-2.5 text-left text-sm text-content hover:bg-surface transition-colors"
                                disabled={isMutating}
                              >
                                Interrumpir
                              </button>
                            )}
                            {popCanRestore && (
                              <button
                                type="button"
                                onClick={handlePopoverRestore}
                                className="w-full px-4 py-2.5 text-left text-sm text-content hover:bg-surface transition-colors"
                                disabled={isMutating}
                              >
                                Restaurar guardia
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={handlePopoverRemove}
                              className="w-full px-4 py-2.5 text-left text-sm text-error hover:bg-surface transition-colors"
                              disabled={isMutating}
                            >
                              Quitar guardia
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={closePopover}
                className="w-8 h-8 rounded-full hover:bg-surface-2 inline-flex items-center justify-center text-content-muted hover:text-content transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-4 pb-4 space-y-3">
              {(() => {
                const rStart = startOfDay(popoverRecord.startDate);
                const rEnd = startOfDay(popoverRecord.endDate || new Date(8640000000000000));
                const today = startOfDay(now);
                const team = teams.find((t) => t.id === popoverRecord.teamId);
                const technicians = techniciansByTeamId[popoverRecord.teamId] || [];
                const popIsInterrupted = Boolean(popoverRecord?.isInterrupted);
                let dotColor;
                if (rEnd < today) dotColor = 'var(--muted-deep)';
                else if (rStart <= today && rEnd >= today) dotColor = 'var(--success)';
                else dotColor = 'var(--amber)';
                return (
                  <>
                    <div className="flex items-start gap-3">
                      <span className="w-3 h-3 rounded-sm mt-1.5 shrink-0" style={{ backgroundColor: dotColor }} />
                      <div>
                        <p className="text-content font-semibold text-lg">{team?.name || 'Equipo asignado'}</p>
                        <div className="mt-1">
                          <TechniciansAvatarGroup technicians={technicians} maxVisible={4} showCount />
                        </div>
                        <p className="text-content-muted text-sm mt-0.5">
                          Semana {popoverWeek.weekNumber} &middot; {formatWeek(popoverWeek.weekStart, popoverWeek.weekEnd)}
                        </p>
                      </div>
                    </div>
                    {popIsInterrupted && (
                      <div className="bg-surface-2/50 rounded-lg p-3">
                        <p className="text-content-muted text-xs uppercase tracking-wide mb-1">Motivo de interrupción</p>
                        <p className="text-content text-sm">{popoverRecord.interruptReason || 'Sin motivo registrado'}</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </>,
        portalTarget,
      )}

      {portalTarget && isAssignModalOpen && selectedWeek && createPortal(
        <div className="fixed inset-0 z-[10020]">
          <div className="absolute inset-0 bg-[var(--app-overlay)]" onClick={closeAssignModal} />
          <div className="relative z-[10021] flex min-h-screen items-center justify-center p-4">
            <div className="w-full max-w-2xl bg-surface rounded-lg shadow-xl border border-line">
            <div className="flex items-start justify-between p-6">
              <h3 className="text-xl md:text-2xl font-bold text-content flex items-center gap-2">
                <CalendarBlank size={22} className="text-content" />
                {isInterruptMode ? 'Interrupción de la guardia' : 'Asignar Equipo de Guardia'}
              </h3>

              <button type="button" onClick={closeAssignModal} className="text-content-muted hover:text-content">
                <X size={18} />
              </button>
            </div>
            
            <div className="px-6 pb-6 space-y-5">
              {isInterruptMode ? (
                <NotificationAlert
                  origin="guard-interrupt-modal"
                  onClose={() => setModalError('')}
                  notification={{
                    show: Boolean(modalError),
                    message: modalError,
                    type: 'error',
                    origin: 'guard-interrupt-modal',
                  }}
                />
              ) : (
                <NotificationAlert
                  origin="guard-assign-modal"
                  onClose={() => setModalError('')}
                  notification={{
                    show: Boolean(modalError),
                    message: modalError,
                    type: 'error',
                    origin: 'guard-assign-modal',
                  }}
                />
              )}
              <div className="bg-surface-2 border border-line rounded-xl p-4">
                <p className="text-content-muted text-sm">Semana {selectedWeek.weekNumber}</p>
                <p className="text-xl md:text-2xl font-semibold text-content mt-1">{formatWeek(selectedWeek.weekStart, selectedWeek.weekEnd)}</p>
              </div>

              {isInterruptMode ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-content mb-2">Día de interrupción *</label>
                    <div className="relative" ref={interruptDatePopupRef}>
                      <button
                        type="button"
                        onClick={() => setIsInterruptDateOpen((prev) => !prev)}
                        className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line text-left flex items-center justify-between"
                      >
                        <span>{formatDisplayDate(interruptEndAt)}</span>
                        <CalendarBlank size={18} className="text-content-muted ml-2" />
                      </button>

                      {isInterruptDateOpen && (
                        <div className="absolute z-[10000] mt-2 w-[320px] max-w-[90vw] rounded-lg border border-line bg-surface-2 p-2 shadow-2xl">
                          <DateCalendar
                            date={parseYmd(interruptEndAt) || new Date()}
                            minDate={parseYmd(selectedRecord?.startAt || toYmd(selectedWeek.weekStart))}
                            maxDate={parseYmd(selectedRecord?.endAt || toYmd(selectedWeek.weekEnd))}
                            onChange={(date) => {
                              setInterruptEndAt(toYmd(date));
                              setIsInterruptDateOpen(false);
                            }}
                            locale={es}
                            color="#f5b731"
                            className="generic-date-range__picker compact-date-picker"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-content mb-2">Motivo</label>
                    <textarea
                      value={interruptReason}
                      onChange={(e) => setInterruptReason(e.target.value)}
                      placeholder="Escribe el motivo de la interrupción"
                      rows={3}
                      className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-content mb-2">Nuevo equipo de guardia *</label>
                    <select
                      value={interruptTeamId}
                      onChange={(e) => setInterruptTeamId(e.target.value)}
                      className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                    >
                      <option value="">Seleccionar equipo</option>
                      {teams.filter((team) => team.id !== selectedRecord?.teamId).map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-content mb-2">Equipo de Guardia *</label>
                  <select
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
                  >
                    <option value="">Seleccionar equipo</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            </div>

              <div className="px-6 pb-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeAssignModal}
                  className="px-6 py-2.5 rounded-xl bg-bg border border-line text-content text-sm md:text-base"
                  disabled={isMutating}
                >
                  Cancelar
                </button>
                {isInterruptMode ? (
                  <button
                    type="button"
                    onClick={handleInterruptTeam}
                    disabled={!interruptTeamId || !interruptEndAt || isMutating}
                    className="px-8 py-2.5 rounded-xl bg-amber hover:bg-amber-hover text-on-amber text-sm md:text-base font-semibold disabled:opacity-50"
                  >
                    {isMutating ? 'Guardando...' : 'Confirmar interrupción'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleAssignTeam}
                    disabled={!selectedTeamId || isMutating}
                    className="px-8 py-2.5 rounded-xl bg-amber hover:bg-amber-hover text-on-amber text-sm md:text-base font-semibold disabled:opacity-50"
                  >
                    {isMutating ? 'Guardando...' : 'Asignar'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        portalTarget,
      )}
    </div>
  );
}
